import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { StyledImage } from './Image';
import { StyledAdvert } from './Advert';
import { StyledSpacer } from './Spacer';
import { StyledVideo } from './Video';
import { StyledDivider } from './Divider';
import { StyledTwitterTimeline } from './Twitter';
import { StyledText } from './Text';
import { StyledInfogram } from './Infogram';
import { StyledGallery } from './Gallery';
import { StyledCustom } from './Custom';
// import { StyledMap } from './Map';

import { ArticleContext } from '../Context';
import styles from '../article.module.css';

export class StyledColumns extends Component {
	static contextType = ArticleContext;
	state = {
		paddingLeft: null,
		paddingRight: null,
		backgroundColor: null,
	};
	componentDidMount() {
		let { styles, id } = this.props;
		if (typeof styles === 'string') {
			styles = [];
		}

		let properties = [];
		for (const id of styles) {
			const style = this.context.styles?.get(id);
			if (style) {
				properties.push(style.properties);
			}
		}

		const css = new CSS(id, properties.reduce(this.reduceProperties, {}));

		this.setState({
			css: css.build(),
		});
	}

	reduceProperties = (acc, properties) => {
		for (const property in properties) {
			// The property do not exist so we just add it
			if (!acc[property]) {
				acc[property] = properties[property];
				continue;
			}

			// The property is an array so we just concatenate
			if (Array.isArray(properties[property])) {
				acc[property] = [...acc[property], ...properties[property]];
				continue;
			}

			acc[property] = { ...acc[property], ...properties[property] };
		}
		return acc;
	};

	byComponent = (component, i) => {
		switch (component.component) {
			case 'headline':
			case 'title':
			case 'subtitle':
			case 'intro':
			case 'body':
			case 'crosshead':
			case 'byline':
			case 'blockquote':
			case 'text1':
			case 'text2':
			case 'text3':
			case 'text4':
			case 'text5':
			case 'text6':
			case 'text7':
			case 'text8':
			case 'text9':
			case 'text10':
			case 'footer':
				return <StyledText key={i} {...component} />;
			case 'image':
				return <StyledImage key={i} {...component} />;
			case 'advert':
				return <StyledAdvert key={i} {...component} />;
			case 'infogram':
				return <StyledInfogram key={i} {...component} />;
			case 'map':
				return null;
			// return <StyledMap key={i}  {...component}/>;
			case 'spacer':
				return <StyledSpacer key={i} {...component} />;
			case 'video':
				return <StyledVideo key={i} {...component} />;
			case 'twitter':
				return <StyledTwitterTimeline key={i} {...component} />;
			case 'divider':
				return <StyledDivider key={i} {...component} />;
			case 'columns':
				return <StyledColumns key={i} {...component} />;
			case 'custom':
				return <StyledCustom key={i} {...component} />;
			case 'gallery':
				if(component['control-speed']) {
					component.control_speed = component['control-speed'];
				}
				return <StyledGallery key={i} {...component} />;
			default:
				return null;
		}
	};

	render() {
		const { device } = this.context;
		const {
			id,
			bleed,
			expandfullwidth,
			backgroundcolor,
			columns,
			imageurl,
			backgroundimage,
			imagepositiontop,
			imagepositionleft,
			imageopacity,
			colsplit,
			collapsetype,
		} = this.props;
		const { css } = this.state;
		let collapse = false;
		if (
			(collapsetype === 'responsive' && device === 'mobile') ||
			(collapsetype === 'tablet' && device === 'tablet')
		) {
			collapse = true;
		}
		return (
			<Columns
				id={id}
				bleed={bleed === 'on'}
				expandFullWidth={expandfullwidth === 'on'}
				backgroundColor={backgroundcolor}
				css={css}
				imageUrl={imageurl}
				collapse={collapse}
				backgroundImage={backgroundimage === 'on'}
				imagePositionTop={imagepositiontop}
				imagePositionLeft={imagepositionleft}
				imageOpacity={imageopacity}
				colSplit={colsplit}
			>
				{columns.map((column, i) => (
					<div key={i}>{column.map(this.byComponent)}</div>
				))}
			</Columns>
		);
	}
}

StyledColumns.propTypes = {
	id: PropTypes.string,
	bleed: PropTypes.string,
	expandfullwidth: PropTypes.string,
	backgroundcolor: PropTypes.string,
	collapsetype: PropTypes.string,
	imageopacity: PropTypes.number,
	imagepositiontop: PropTypes.number,
	imageurl: PropTypes.string,
	backgroundimage: PropTypes.string,
	imagepositionleft: PropTypes.number,
	columns: PropTypes.array,
	colsplit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	styles: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
};

export const Columns = ({
	id,
	collapse,
	children,
	css,
	colSplit,
	backgroundColor,
	imageUrl,
	backgroundImage,
	imagePositionTop,
	imagePositionLeft,
	imageOpacity,
}) => {
	if (!children) {
		return null;
	}

	if (!children.length) {
		return null;
	}

	const total = children.length;

	const gridTemplateColumns = getColSplit(total, colSplit);
	const style = {
		gridTemplateColumns,
	};

	let classNames = [
		styles[`columns`],
		styles[`column-${total}`],
		`columns-${id}`,
	];
	if (collapse) {
		classNames.push(styles['collapse']);
		style.display = 'flex';
	}

	if (backgroundImage && imageUrl) {
		backgroundColor = backgroundColor.replaceAll('#', '');
		backgroundColor = `#${backgroundColor}`;
		imageUrl = encodeURI(imageUrl).replaceAll('%5C', '/');
		const rgba = hexToRGB(backgroundColor, parseFloat(imageOpacity) / 100);
		style.background = `linear-gradient(${rgba}, ${rgba}), url("${imageUrl}")`;
		style.backgroundPosition = `${imagePositionTop}% ${imagePositionLeft}%`;
	}

	return (
		<section id={id} className={classNames.join(' ')} style={style}>
			{children}
			{css ? <style jsx>{css}</style> : null}
		</section>
	);
};

Columns.propTypes = {
	id: PropTypes.string,
	columns: PropTypes.array,
	collapse: PropTypes.bool,
	css: PropTypes.string,
	colSplit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	backgroundColor: PropTypes.string,
	imageOpacity: PropTypes.number,
	imagePositionTop: PropTypes.number,
	imagePositionLeft: PropTypes.number,
	imageUrl: PropTypes.string,
	backgroundImage: PropTypes.bool,
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]),
};

Columns.defaultProps = {
	style: {},
	css: '',
};

function getColSplit(total, colSplit) {
	if (total === 1) {
		return '1fr';
	}

	return colSplit
		.split('-')
		.map((split) => `${split}fr`)
		.join(' ');
}

function hexToRGB(hex, alpha) {
	var r = parseInt(hex.slice(1, 3), 16),
		g = parseInt(hex.slice(3, 5), 16),
		b = parseInt(hex.slice(5, 7), 16);

	if (alpha) {
		return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
	} else {
		return 'rgb(' + r + ', ' + g + ', ' + b + ')';
	}
}

export const Column = ({ id, components }) => {
	const byComponent = (component, i) => {
		if (component.expandfullwidth === 'on') {
			component.expandfullwidth = 'off';
		}
		switch (component.component) {
			case 'headline':
			case 'title':
			case 'subtitle':
			case 'intro':
			case 'body':
			case 'crosshead':
			case 'byline':
			case 'blockquote':
			case 'text1':
			case 'text2':
			case 'text3':
			case 'text4':
			case 'text5':
			case 'text6':
			case 'text7':
			case 'text8':
			case 'text9':
			case 'text10':
			case 'footer':
				return <StyledText key={i} {...component} />;
			case 'image':
				return <StyledImage key={i} {...component} />;
			case 'infogram':
				return <StyledInfogram key={i} {...component} />;
			case 'map':
				return null;
			// return <StyledMap key={i}  {...component}/>;
			case 'spacer':
				return <StyledSpacer key={i} {...component} />;
			case 'video':
				return <StyledVideo key={i} {...component} />;
			case 'twitter':
				return <StyledTwitterTimeline key={i} {...component} />;
			case 'divider':
				return <StyledDivider key={i} {...component} />;
			default:
				return null;
		}
	};
	return <div id={id}>{components.map(byComponent)}</div>;
};

Column.propTypes = {
	id: PropTypes.string,
	components: PropTypes.array,
};

class CSS {
	constructor(id, properties) {
		this.id = id;
		this.properties = properties;
	}

	build() {
		const { paddingLeft, paddingRight } = this.getBleedPadding();
		const canvas = this.getCanvasCSS(paddingLeft, paddingRight);
		const caption = this.getCaptionCSS();
		const text = this.getTextCSS();
		const anchor = this.getAnchorCSS();
		const spacer = this.getSpacerCSS();

		const css = [canvas, caption, text, anchor, spacer]
			.filter((i) => i)
			.join('\n');

		return css;
	}

	getBleedPadding = () => {
		if (!this.properties?.canvas) {
			return {
				paddingLeft: '0px',
				paddingRight: '0px',
			};
		}
		let { paddingLeft, paddingRight, spacingUnit } =
			this.properties?.canvas;

		if (!spacingUnit) {
			spacingUnit = 'px';
		}

		return {
			paddingLeft: paddingLeft ? `${paddingLeft}${spacingUnit}` : '0px',
			paddingRight: paddingRight
				? `${paddingRight}${spacingUnit}`
				: '0px',
		};
	};

	getCanvasCSS = (paddingLeft, paddingRight) => {
		const id = this.id;
		if (!this.properties?.canvas) {
			return '';
		}
		let { color, paddingTop, paddingBottom } = this.properties?.canvas;

		color = color ? `background-color: ${color};` : '';
		paddingTop = paddingTop ? `padding-top: ${paddingTop};` : '';
		paddingBottom = paddingBottom
			? `padding-bottom: ${paddingBottom};`
			: '';

		paddingLeft = `padding-left: ${paddingLeft};`;
		paddingRight = `padding-right: ${paddingRight};`;
		return `#${id} {
			${color}
			${paddingLeft}
			${paddingRight}
			${paddingTop}
			${paddingBottom}
		}`;
	};

	getCaptionCSS = () => {
		const id = this.id;
		if (!this.properties?.imagecaption) {
			return '';
		}
		let {
			color,
			fontFamily,
			fontSize,
			fontStyle,
			fontWeight,
			letterSpacing,
			lineHeight,
			marginBottom,
			textAlign,
			textDecoration,
			textTransform,
		} = this.properties?.imagecaption;

		color = color ? `color: ${color};` : '';
		fontFamily = fontFamily ? `font-family: ${fontFamily};` : '';
		fontSize = fontSize ? `font-size: ${fontSize}em;` : '';
		fontStyle = fontStyle ? `font-style: ${fontStyle};` : '';
		fontWeight = fontWeight ? `font-weight: ${fontWeight};` : '';
		letterSpacing = letterSpacing
			? `letter-spacing: ${parseFloat(letterSpacing)}em;`
			: '';
		lineHeight = lineHeight ? `line-height: ${lineHeight};` : '';
		marginBottom = marginBottom ? `margin-bottom: ${marginBottom};` : '';
		textAlign = textAlign ? `text-align: ${textAlign};` : '';
		textDecoration = textDecoration
			? `text-decoration: ${textDecoration};`
			: '';
		textTransform = textTransform
			? `text-transform: ${textTransform};`
			: '';

		return `#${id} figure figcaption {
			${color}
			${fontFamily}
			${fontSize}
			${fontStyle}
			${fontWeight}
			${letterSpacing}
			${lineHeight}
			${marginBottom}
			${textAlign}
			${textDecoration}
			${textTransform}
		}`;
	};

	getCreditCSS = () => { };

	getTextCSS = () => {
		const id = this.id;
		const css = [''];
		const textComponents = [
			'headline',
			'title',
			'subtitle',
			'intro',
			'body',
			'crosshead',
			'byline',
			'blockquote',
			'footer',
			'text1',
			'text2',
			'text3',
			'text4',
			'text5',
			'text6',
			'text7',
			'text8',
			'text9',
			'text10',
		];
		// Mobile
		for (const component in this.properties) {
			// const properties = style.properties[property];
			// The component is not a text so we ignore it
			if (
				!textComponents.includes(component) ||
				!this.properties[component]
			) {
				continue;
			}

			const properties = this.properties[component];

			let {
				fontFamily,
				fontSize,
				color,
				fontWeight,
				fontStyle,
				textDecoration,
				lineHeight,
				textTransform,
				textAlign,
				letterSpacing,
				marginBottom,
				marginTop,
				textDisplay,
				// Dropcap
				dropcapFontSize,
				dropcapLineHeight,
				dropcapPaddingRight,
				dropcapColor,
				// Font Weight
				fontFamilyBold,
				fontFamilyItalic,
				fontFamilyBoldItalic,
			} = properties;

			textDisplay = textDisplay
			? `display: ${textDisplay};`
			: '';

			if (properties.css) {
				css.push(`#${id} > .${component} {
					${properties.css};
				}`);
			}

			css.push(`#${id} .${component} p {
				margin: 0px;
				max-width: 100%;
				${textDisplay}
			}
			#${id} .${component} > sub {
				font-size: 65%;
				line-height: 0;
				vertical-align: sub;
			}
			#${id} ${component} > sup{
				font-size: 65%;
				line-height: 0;
				vertical-align: super;
			}
			#${id} .${component} i {
				font-style: italic;
			}`);

			fontFamily = fontFamily ? `font-family: ${fontFamily};` : '';
			fontSize = fontSize ? `font-size: ${fontSize}em;` : '';
			color = color ? `color: ${color};` : '';
			fontWeight = fontWeight ? `font-weight: ${fontWeight};` : '';
			fontStyle = fontStyle ? `font-style: ${fontStyle};` : '';
			textDecoration = textDecoration
				? `text-decoration: ${textDecoration};`
				: '';
			lineHeight = lineHeight ? `line-height: ${lineHeight};` : '';
			textTransform = textTransform
				? `text-transform: ${textTransform};`
				: '';
			textAlign = textAlign ? `text-align: ${textAlign};` : '';
			letterSpacing = letterSpacing
				? `letter-spacing: ${parseFloat(letterSpacing)}em;`
				: '';
			marginBottom = marginBottom
				? `margin-bottom: ${marginBottom}px;`
				: '';
			marginTop = marginTop ? `margin-top: ${marginTop};` : '';

			dropcapLineHeight =
				dropcapLineHeight && dropcapFontSize
					? `line-height: ${parseFloat(dropcapFontSize + '') *
					parseFloat(dropcapLineHeight + '')
					}em;`
					: '';
			dropcapFontSize = dropcapFontSize
				? `font-size: ${parseFloat(dropcapFontSize + '')}em;`
				: 'font-size: 4em;';
			dropcapPaddingRight = dropcapPaddingRight
				? `padding-right: ${dropcapPaddingRight}px;`
				: 'padding-right: 8px;';
			dropcapColor = dropcapColor ? `color: ${dropcapColor};` : '';

			css.push(`#${id} > div > .${component} {
				overflow-wrap: break-word;
				${fontFamily}
				${fontSize}
				${color}
				${fontWeight}
				${fontStyle}
				${textDecoration}
				${lineHeight}
				${textTransform}
				${textAlign}
				${letterSpacing}
				${marginBottom}
				${marginTop}
			}`);
			css.push(`#${id} .${component}.dropcap > :first-child::first-letter {
				float: left;
				margin-left: -2px;
				${dropcapFontSize}
				${dropcapLineHeight}
				${dropcapPaddingRight}
				${dropcapColor}
			}`);
			if (fontFamilyBold) {
				css.push(`#${id} .${component} > p b {
					font-family: ${fontFamilyBold} !important;
					font-weight: normal;
				}`);
			}

			if (fontFamilyItalic) {
				css.push(`#${id} .${component} > p i {
					font-family: ${fontFamilyItalic};
					font-style: normal;
				}`);
			}

			if (fontFamilyBoldItalic) {
				css.push(`#${id} .${component} > p b i {
					font-family: ${fontFamilyBoldItalic};
					font-weight: normal;
					font-style: normal;
				}`);
			}

			/*console.log(`Property: ${property}`);
			console.table(properties);*/
		}
		return css.join('\n');
	};

	getAnchorCSS = () => {
		const id = this.id;
		if (!this.properties['anchor']) {
			return '';
		}
		let { fontStyle, color, fontWeight, textDecoration, textTransform } =
			this.properties['anchor'];

		color = color ? `color: ${color};` : '';
		fontStyle = fontStyle ? `font-style: ${fontStyle};` : '';
		fontWeight = fontWeight ? `font-weight: ${fontWeight};` : '';
		textDecoration = textDecoration
			? `text-decoration: ${textDecoration};`
			: '';
		textTransform = textTransform
			? `text-transform: ${textTransform};`
			: '';

		return `#${id} a {
			${color}
			${fontStyle}
			${fontWeight}
			${textDecoration}
			${textTransform}
		}`;
	};

	getSpacerCSS = () => {
		const id = this.id;
		const css = [];
		if (!this.properties.spacer) {
			return '';
		}
		let {
			vSmallHeight,
			smallHeight,
			mediumHeight,
			largeHeight,
			vLargeHeight,
		} = this.properties['spacer'];

		if (vSmallHeight) {
			css.push(`#${id} .spacer .v-small {
				height: ${vSmallHeight}px;
			}`);
		}
		if (smallHeight) {
			css.push(`#${id} .spacer.small {
				height: ${smallHeight}px;
			}`);
		}
		if (mediumHeight) {
			css.push(`#${id} .spacer.medium {
				height: ${mediumHeight}px;
			}`);
		}
		if (largeHeight) {
			css.push(`#${id} .spacer.large {
				height: ${largeHeight}px;
			}`);
		}
		if (vLargeHeight) {
			css.push(`#${id}.spacer.v-large {
				height: ${vLargeHeight}px;
			}`);
		}

		return css.join('\n');
	};
}
